// lib/tonnageAggregation.ts
import { TonnageRow } from '../types';

export interface AirlineRow {
    airlineCode: string;
    y1: number;
    y2: number;
    diff: number;
    pct: number | null;
}

export interface UnitGroup {
    unit: string;
    airlines: AirlineRow[];
    y1Total: number;
    y2Total: number;
    diff: number;
    pct: number | null;
}

export interface PanelData {
    groups: UnitGroup[];
    grandTotal: { y1: number; y2: number; diff: number; pct: number | null };
}

type RowFilter = (row: TonnageRow) => boolean;

function pctChange(y1: number, y2: number): number | null {
    if (!y1) return null;
    return (y2 - y1) / y1;
}

function sumByUnitAirline(
    rows: TonnageRow[],
    filter: RowFilter,
    selectedUnits: string[],
    selectedAirlines: string[]
): Map<string, Map<string, number>> {
    const map = new Map<string, Map<string, number>>();
    for (const row of rows) {
        if (!filter(row)) continue;
        if (selectedUnits.length && !selectedUnits.includes(row.unit)) continue;
        if (selectedAirlines.length && !selectedAirlines.includes(row.airlineCode)) continue;

        if (!map.has(row.unit)) map.set(row.unit, new Map());
        const unitMap = map.get(row.unit)!;
        unitMap.set(row.airlineCode, (unitMap.get(row.airlineCode) ?? 0) + row.flightKg);
    }
    return map;
}

// filterY1/filterY2 are separate because Week One / Week Two (or Month One / Month Two)
// can point at different values — that's the flexibility you wanted.
export function buildPanel(
    rowsY1: TonnageRow[],
    rowsY2: TonnageRow[],
    filterY1: RowFilter,
    filterY2: RowFilter,
    selectedUnits: string[],
    selectedAirlines: string[]
): PanelData {
    const mapY1 = sumByUnitAirline(rowsY1, filterY1, selectedUnits, selectedAirlines);
    const mapY2 = sumByUnitAirline(rowsY2, filterY2, selectedUnits, selectedAirlines);

    const units = Array.from(new Set([...mapY1.keys(), ...mapY2.keys()])).sort();
    const groups: UnitGroup[] = [];
    let grandY1 = 0;
    let grandY2 = 0;

    for (const unit of units) {
        const y1Airlines = mapY1.get(unit) ?? new Map<string, number>();
        const y2Airlines = mapY2.get(unit) ?? new Map<string, number>();
        const codes = Array.from(new Set([...y1Airlines.keys(), ...y2Airlines.keys()])).sort();

        let unitY1 = 0;
        let unitY2 = 0;
        const airlines: AirlineRow[] = codes.map((code) => {
            const y1 = y1Airlines.get(code) ?? 0;
            const y2 = y2Airlines.get(code) ?? 0;
            unitY1 += y1;
            unitY2 += y2;
            return { airlineCode: code, y1, y2, diff: y2 - y1, pct: pctChange(y1, y2) };
        });

        groups.push({ unit, airlines, y1Total: unitY1, y2Total: unitY2, diff: unitY2 - unitY1, pct: pctChange(unitY1, unitY2) });
        grandY1 += unitY1;
        grandY2 += unitY2;
    }

    return { groups, grandTotal: { y1: grandY1, y2: grandY2, diff: grandY2 - grandY1, pct: pctChange(grandY1, grandY2) } };
}